"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const _ = require("lodash");
const rxjs_1 = require("rxjs");
const notifications_service_g_1 = require("./notifications-service.g");
const utils_1 = require("../utils");
const config_1 = require("../config");
const subscription_params_1 = require("./subscription-params");
/**
 * Notification microservice wrapper class.  Used for managing subscriptions, sending publications, and receiving events to a device.
 * @class Notify
 */
class NotificationsService extends notifications_service_g_1.NotificationsService {
    /**
     * Creates an instance of notify for the given device ID.
     *
     * @param {string} version optional notifications service version override.
     * @param {string} cloud optional target cloud.
     * @param {string} stage optional target stage override.
     * @param {string} url optional base url override.
     * @param {string} http optional http fetch override.
     * @param {MqttSocket} mqttSocket optional mqtt socket management instance.  Required for websocket functions.
     * @param {IsometricMobileNotify[]} mobileNotifications optional mobile notifications instance.  Required for mobile push.
     * @param {() => Promise<Token>)} getAuthToken optional reconnect auth token fetch override.
     */
    constructor(params = {}) {
        super(params);
        this.mobileTransportMap = {};
        this.mqttSocket = params.mqttSocket;
        this.getAuthToken = params.getAuthToken;
        this.diagnosticsObservable = new rxjs_1.Subject();
        this.messageObservable = new rxjs_1.Subject();
        this.diagnosticSubscription('Notify', 'Message', this.messageObservable);
        (params.mobileNotifiers || []).forEach(mobileNotify => {
            this.mobileTransportMap[mobileNotify.getTransport()] = mobileNotify;
            mobileNotify.asTokenObservable()
                .subscribe(newToken => this.onMobileTokenChanged(newToken));
            this.diagnosticSubscription('MobileNotify', 'TokenChange', mobileNotify.asTokenObservable());
            this.diagnosticSubscription('MobileNotify', 'Message', mobileNotify.asMessageObservable());
            mobileNotify.asMessageObservable()
                .map(this.extractPublication)
                .filter(publication => publication !== undefined)
                .subscribe(publication => this.pushRxEvent(this.messageObservable, publication, 'Notify', 'Message'));
        });
        if (this.mqttSocket) {
            this.diagnosticSubscription('MqttSocket', 'Connectivity', this.mqttSocket.asConnectivityObservable());
            this.diagnosticSubscription('MqttSocket', 'Message', this.mqttSocket.asMessageObservable());
            this.diagnosticSubscription('MqttSocket', 'Websocket', this.mqttSocket.asWebsocketObservable());
            this.mqttSocket.asMessageObservable().subscribe(message => this.pushRxEvent(this.messageObservable, JSON.parse(message.payloadString), 'MqttSocket', 'Message'));
        }
    }
    /**
     * Get or create a subscription to a transport with the given topic and subject filter.
     *
     * @param {SubscribeParams} params params for the subscription.
     */
    subscribe(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const request = new subscription_params_1.SubscriptionParams(params);
            switch (params.transport) {
                case 'fcm':
                case 'apns':
                case 'apns-voip': {
                    return yield this.subscribeToMobile(request);
                }
                case 'websocket': {
                    return yield this.subscribeToWebsocket(request);
                }
                default: {
                    if (!request.deviceId) {
                        throw new Error('Non-device transports must specifiy a targetDevice');
                    }
                    else {
                        return yield this.subscribeToTransport(request);
                    }
                }
            }
        });
    }
    /**
     * Unsubscribe the subscription for the given ID.
     *
     * @param {string} params.subscriptionId ID of the subscription to delete.
     */
    unsubscribe(params) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let subscription = yield this.getSubscription({ subscriptionId: params.subscriptionId });
                if (this.mqttSocket && this.mqttSocket.isConnected()) {
                    yield this.mqttSocket.unsubscribe({ subscriptionId: subscription.subscriptionId });
                }
                yield this.deleteSubscription({ subscriptionId: params.subscriptionId });
                return subscription;
            }
            catch (error) {
                if (error.statusCode === 404) {
                    return { subscriptionId: params.subscriptionId };
                }
                else {
                    throw error;
                }
            }
        });
    }
    /**
     * Unsubscribes device from all subscriptions.
     */
    unsubscribeDevice(_params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const subscriptions = yield this.getDeviceSubscriptions();
            return yield Promise.all(subscriptions.map(subscription => this.unsubscribe({ subscriptionId: subscription.subscriptionId })));
        });
    }
    /**
     * Get the subscriptions for this device.
     */
    getDeviceSubscriptions(_params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const deviceIds = [];
            for (const key in this.mobileTransportMap) {
                deviceIds.push(yield this.mobileTransportMap[key].getToken());
            }
            if (this.mqttSocket) {
                deviceIds.push(this.mqttSocket.getClientId());
            }
            return this.getSubscriptionsForDevices(deviceIds);
        });
    }
    /**
     * Connects and subscribes over MQTT based on existing subscriptions for this device.
     */
    reestablishMqttSubscriptions() {
        return __awaiter(this, void 0, void 0, function* () {
            if (yield this.hasToken()) {
                const mqttSocket = this.getMqttSocket();
                const mqttSubscriptions = yield this.getSubscriptionsForDevice(mqttSocket.getClientId());
                yield this.connectMqttSocket();
                return yield Promise.all(mqttSubscriptions.map(({ subscriptionId }) => mqttSocket.subscribe({ subscriptionId })));
            }
            else {
                const error = new Error('No auth token available to reestablish MQTT connection');
                this.pushDiagnosticsEvent({ category: 'MqttSocket', type: 'Connectivity', isError: true, data: error });
                throw error;
            }
        });
    }
    /**
     * Get the subscriptions for this user.
     */
    getUserSubscriptions(_params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const subscriptions = [];
            let odata = {};
            do {
                const collection = yield this.getSubscriptions(odata);
                subscriptions.push(...utils_1.Utils.getItemsFromCollection(collection));
                odata = utils_1.Utils.getOdataNext(collection);
            } while (odata);
            return subscriptions;
        });
    }
    /**
     * Provides a diagnostic stream of messages from the SDK.
     */
    asDiagnosticsObservable() {
        return this.diagnosticsObservable;
    }
    /**
     * Provides a messaging stream for notifications received from client transport subscriptions.
     */
    asMessageObservable() {
        return this.messageObservable;
    }
    /**
     * Disconnects websocket and the attached retry loop.
     */
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.mqttSocket) {
                yield this.mqttSocket.disconnect();
            }
            if (this.reconnectLoop) {
                this.reconnectLoop.reset();
            }
        });
    }
    subscribeToTransport(params) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.createSubscription({
                body: Object.assign({}, (params instanceof subscription_params_1.SubscriptionParams ? params : new subscription_params_1.SubscriptionParams(params))),
            });
        });
    }
    getSubscriptionsForDevice(deviceId) {
        return this.getSubscriptionsForDevices([deviceId]);
    }
    getSubscriptionsForDevices(deviceIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const subscriptions = yield this.getUserSubscriptions();
            return subscriptions.filter(subscription => deviceIds.some((value, _index, _array) => subscription.deviceId === value));
        });
    }
    subscribeToWebsocket(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const mqttSocket = this.getMqttSocket();
            const subscription = yield this.subscribeToTransport(Object.assign(Object.assign({}, params), { transport: 'websocket', deviceId: mqttSocket.getClientId() }));
            yield this.connectMqttSocket();
            yield mqttSocket.subscribe({ subscriptionId: subscription.subscriptionId });
            return subscription;
        });
    }
    connectMqttSocket() {
        const mqttSocket = this.getMqttSocket();
        if (!this.mqttConnectionPromise && !mqttSocket.isConnected() && !mqttSocket.isConnecting()) {
            this.mqttConnectionPromise = this.startMqttConnection(mqttSocket);
        }
        return this.mqttConnectionPromise || Promise.resolve(mqttSocket);
    }
    startMqttConnection(mqttSocket) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const connection = yield this.createConnection({ body: { transport: 'websocket' } });
                yield mqttSocket.connect({ url: connection.websocketUrl });
                this.mqttConnectionPromise = undefined;
                return mqttSocket;
            }
            catch (error) {
                this.mqttConnectionPromise = undefined;
                throw error;
            }
        });
    }
    getMqttSocket() {
        if (!this.mqttSocket) {
            throw new ReferenceError('mqttSocket is not defined');
        }
        else {
            return this.mqttSocket;
        }
    }
    subscribeToMobile(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const mobileNotify = this.mobileTransportMap[params.transport];
            if (!mobileNotify) {
                throw new Error(`MobileNotify instance was not setup for [${params.transport}] transport.`);
            }
            else {
                const token = yield mobileNotify.getToken();
                return this.subscribeToTransport(Object.assign(Object.assign({}, params), { deviceId: token, applicationId: mobileNotify.getApplicationId() }));
            }
        });
    }
    onMobileTokenChanged(tokenEvent) {
        return __awaiter(this, void 0, void 0, function* () {
            if (tokenEvent.oldToken) {
                try {
                    const subscriptions = yield this.getSubscriptionsForDevice(tokenEvent.oldToken);
                    const newSubscriptions = yield Promise.all(subscriptions.map(subscription => this.resubscribeWithNewDeviceId(subscription)));
                    this.diagnosticsObservable.next({
                        category: 'MobileNotify',
                        type: 'Resubscribe',
                        isError: false,
                        data: _.flatten(newSubscriptions),
                    });
                }
                catch (error) {
                    this.diagnosticsObservable.next({
                        category: 'MobileNotify',
                        type: 'TokenChange',
                        isError: true,
                        data: error,
                    });
                }
            }
        });
    }
    resubscribeWithNewDeviceId(subscription) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.unsubscribe({ subscriptionId: subscription.subscriptionId });
            return yield this.subscribeToMobile(subscription);
        });
    }
    diagnosticSubscription(category, type, observable) {
        observable.subscribe(event => this.pushDiagnosticsEvent({ category, type, isError: false, data: event }), error => this.pushDiagnosticsEvent({ category, type, isError: true, data: error }));
    }
    pushDiagnosticsEvent(event) {
        this.pushRxEvent(this.diagnosticsObservable, event, event.category, event.type);
    }
    pushRxEvent(subject, event, category, type) {
        try {
            subject.next(event);
        }
        catch (error) {
            if (subject !== this.diagnosticsObservable) {
                this.diagnosticsObservable.next({ category, type, isError: true, data: error });
            }
            else {
                //note (JQ) swallow error
            }
        }
    }
    extractPublication(notification) {
        let publicationPayload = undefined;
        if (notification.payload) {
            publicationPayload = notification.payload;
        }
        else if (notification.data && notification.data.payload) {
            publicationPayload = notification.data.payload;
        }
        else if (notification.default) {
            publicationPayload = notification.default;
        }
        if (!publicationPayload || (publicationPayload instanceof Object)) {
            return publicationPayload;
        }
        else {
            return JSON.parse(publicationPayload);
        }
    }
    hasToken() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.getAuthToken) {
                this.getAuthToken = config_1.Config.authentication.getToken.bind(config_1.Config.authentication);
            }
            const token = yield this.getAuthToken();
            return token != null;
        });
    }
}
exports.NotificationsService = NotificationsService;
//# sourceMappingURL=notifications-service.js.map